import React from 'react';
import { Card, Label, Icon, Button } from 'semantic-ui-react';
import { NavLink } from 'react-router-dom';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FeedItem } from '../../app/models/Playground';

interface UrgencyCardProps { item: FeedItem; }

export function UrgencyCard({ item }: UrgencyCardProps) {
  const { t } = useTranslation();
  if (!item.game) return null;

  const minutesLeft = differenceInMinutes(item.game.dateTimeStart, new Date());
  const isVeryUrgent = item.game.spotsLeft === 1 || minutesLeft < 60;

  // Semantic UI color name for labels/buttons
  const urgencyColor: 'red' | 'orange' | 'yellow' =
    item.game.spotsLeft === 1 ? 'red' :
    item.game.spotsLeft <= 2 ? 'orange' :
    'yellow';

  // Hex for the card top border accent
  const urgencyHex =
    item.game.spotsLeft === 1 ? '#db2828' :
    item.game.spotsLeft <= 2 ? '#f2711c' :
    '#fbbd08';

  return (
    <Card
      fluid
      style={{
        borderRadius: 12,
        borderTop: `3px solid ${urgencyHex}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <Card.Content>
        {/* ── Top row: spots badge + time-ago ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Label color={urgencyColor} size='mini' style={{ borderRadius: 20 }}>
            <Icon name={item.game.spotsLeft === 1 ? 'fire' : 'lightning'} />
            {item.game.spotsLeft === 1
              ? t('playground.urgency.oneSpotLeft')
              : t('playground.urgency.spotsLeft', { count: item.game.spotsLeft })}
          </Label>
          <span style={{ fontSize: '0.76em', color: '#aaa' }}>
            <Icon name='clock outline' color='grey' size='small' />
            {formatDistanceToNow(item.game.dateTimeStart, { addSuffix: true })}
          </span>
        </div>

        {/* ── Main row: info + join button ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>

            {/* Game name */}
            <div style={{ fontWeight: 700, fontSize: '0.97em', marginBottom: 4, color: '#1a1a2e' }}>
              {isVeryUrgent && <Icon name='fire' color='red' />}
              {item.game.gameName}
            </div>

            {/* Sport + distance */}
            <div style={{ fontSize: '0.82em', color: '#555', marginBottom: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>
                <Icon name='soccer' color='grey' size='small' />
                {item.game.sportName}
              </span>
              {item.game.distanceKm != null && (
                <span>
                  <Icon name='map marker alternate' color='teal' size='small' />
                  {item.game.distanceKm} km
                </span>
              )}
            </div>

            {/* Participants + time-left warning */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Label size='mini' basic color='grey' style={{ borderRadius: 20 }}>
                <Icon name='users' />
                {item.game.currentParticipants}/{item.game.maxParticipants ?? '?'}
              </Label>
              {minutesLeft < 60 && (
                <Label size='mini' color='red' style={{ borderRadius: 20 }}>
                  <Icon name='hourglass half' />
                  {minutesLeft}m {t('playground.urgency.left')}
                </Label>
              )}
            </div>
          </div>

          {/* Join button */}
          <Button
            as={NavLink}
            to={`/games/${item.game.id}`}
            color={urgencyColor}
            size='small'
            style={{ borderRadius: 20, alignSelf: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <Icon name='sign in' />
            {t('playground.urgency.join')}
          </Button>
        </div>
      </Card.Content>

      {/* ── Footer: start time ── */}
      <Card.Content extra style={{ background: '#fafafa', paddingTop: 6, paddingBottom: 6 }}>
        <span style={{ fontSize: '0.78em', color: '#666' }}>
          <Icon name='calendar alternate outline' color='grey' size='small' />
          {item.game.dateTimeStart.toLocaleString()}
        </span>
      </Card.Content>
    </Card>
  );
}